import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import Header from '../components/Header'
import MenuBar from '../components/MenuBar'
import Label from '../components/Label'
import { showNotificationPopup } from '../components/NotificationPopup'
import { toast } from 'react-toastify'
import { invoiceApi, invoiceError, formatMoney } from '../components/invoicing/api'
import { formatDate } from '../components/helpers/formatDate'
import './InvoicesPage.css'

const labels = { reserved: 'Queued for SBB', creating: 'Creating draft', draft: 'Draft created', invoiced: 'Invoiced',
  failed: 'Not created', unknown: 'Outcome unknown', cancelled: 'Cancelled in SBB', rejected: 'Rejected in SBB' }
const statusColors = { draft: 'blue', invoiced: 'green', failed: 'red', unknown: 'orange', cancelled: 'gray', rejected: 'red' }

const cents = value => Math.round(Number(value) * 100)
const roundCents = value => Math.sign(value) * Math.round(Math.abs(value))
const invoiceAmounts = lines => {
  const rows = lines.map(line => ({ excl: cents(line.amount), vat: 0, rate: Number(line.tax_rate) }))
  const rates = [...new Set(rows.map(row => row.rate))]
  for (const rate of rates) {
    const indexes = rows.map((row, index) => row.rate === rate ? index : -1).filter(index => index >= 0)
    const totalExcl = indexes.reduce((sum, index) => sum + rows[index].excl, 0)
    const totalVat = roundCents(totalExcl * rate / 100)
    const remainders = indexes.map(index => {
      const rawVat = rows[index].excl * rate / 100
      rows[index].vat = Math.trunc(rawVat)
      return { index, remainder: rawVat - rows[index].vat }
    })
    const allocated = indexes.reduce((sum, index) => sum + rows[index].vat, 0)
    const difference = totalVat - allocated
    remainders.sort((a, b) => difference > 0 ? b.remainder - a.remainder || a.index - b.index
      : a.remainder - b.remainder || a.index - b.index)
    for (let i = 0; i < Math.abs(difference); i += 1) rows[remainders[i].index].vat += Math.sign(difference)
  }
  return rows.map(row => ({ excl: row.excl, vat: row.vat, incl: row.excl + row.vat }))
}

function InvoiceLines({ lines = [] }) {
  const amounts = invoiceAmounts(lines)
  const totals = amounts.reduce((sum, row) => ({ excl: sum.excl + row.excl, vat: sum.vat + row.vat, incl: sum.incl + row.incl }),
    { excl: 0, vat: 0, incl: 0 })
  return <div className="invoice-lines"><div className="invoice-lines-scroll">
    <div className="invoice-line invoice-line-header"><span>Description</span><span>Excl. VAT</span><span>VAT %</span><span>VAT amount</span><span>Total</span></div>
    {lines.map((line, index) => <div className="invoice-line" key={index}><span>{line.description}{line.unit_reference && <small>{line.unit_reference}</small>}</span>
      <span>{formatMoney(amounts[index].excl / 100)}</span><span>{line.tax_rate}%</span>
      <span>{formatMoney(amounts[index].vat / 100)}</span><span>{formatMoney(amounts[index].incl / 100)}</span></div>)}
    <div className="invoice-line invoice-line-total"><span>Total</span><span>{formatMoney(totals.excl / 100)}</span><span />
      <span>{formatMoney(totals.vat / 100)}</span><span>{formatMoney(totals.incl / 100)}</span></div>
  </div></div>
}

export default function InvoicesPage() {
  const [params, setParams] = useSearchParams()
  const poId = params.get('po')
  const draftRequested = params.get('draft') === '1'
  const kind = poId || params.get('kind') === 'po' ? 'po' : 'instalments'
  const [accounts, setAccounts] = useState([])
  const [enabled, setEnabled] = useState(false)
  const [proposals, setProposals] = useState(null)
  const [selected, setSelected] = useState({})
  const [poPreviewError, setPoPreviewError] = useState('')
  const [history, setHistory] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState('')

  const loadHistory = async () => {
    const filter = poId ? `po_id=${encodeURIComponent(poId)}` : `kind=${kind}`
    const data = await invoiceApi(`/invoices?page=${page}&${filter}`, null, 'get')
    setHistory(data.invoices); setTotal(data.total)
  }
  useEffect(() => {
    invoiceApi('/invoices/accounts', null, 'get').then(data => {
      setAccounts(data.accounts); setEnabled(data.creation_enabled)
    }).catch(e => toast.error(invoiceError(e)))
  }, [])
  useEffect(() => { loadHistory().catch(e => toast.error(invoiceError(e))) }, [page, poId, kind])
  useEffect(() => {
    if (!history.some(invoice => ['reserved', 'creating'].includes(invoice.status))) return
    const timer = setInterval(() => loadHistory().catch(e => toast.error(invoiceError(e))), 5000)
    return () => clearInterval(timer)
  }, [history, page, poId, kind])
  useEffect(() => { setPage(1); setProposals(null); setSelected({}); setPoPreviewError('') }, [poId, kind])

  const run = async (key, action) => {
    setBusy(key)
    try { await action() } catch (e) { toast.error(invoiceError(e)) } finally { setBusy('') }
  }
  const applyPreview = data => {
    setProposals(data.proposals)
    data.errors.forEach(issue => toast.error(`${issue.unit_name || issue.buyer || 'Invoice'}: ${issue.error}`))
    setSelected(Object.fromEntries(data.proposals.map(p => [p.id, true])))
    if (!data.proposals.length && !data.errors.length) toast.info('No eligible invoices to draft yet.')
  }
  const generatePreview = () => {
    setPoPreviewError('')
    if (poId) setProposals(null)
    setBusy('preview')
    invoiceApi('/invoices/preview', { po_id: poId })
      .then(applyPreview)
      .catch(error => { if (poId) setPoPreviewError(invoiceError(error)); else toast.error(invoiceError(error)) })
      .finally(() => setBusy(''))
  }
  useEffect(() => {
    if (!poId || !draftRequested) return
    let active = true
    setBusy('preview')
    setPoPreviewError('')
    invoiceApi('/invoices/preview', { po_id: poId })
      .then(data => { if (active) applyPreview(data) })
      .catch(error => { if (active) setPoPreviewError(invoiceError(error)) })
      .finally(() => { if (active) setBusy('') })
    return () => { active = false }
  }, [poId, draftRequested])
  const closePreview = () => {
    setProposals(null); setPoPreviewError('')
    if (poId && draftRequested) setParams({ po: poId }, { replace: true })
  }
  const chosen = (proposals || []).filter(p => selected[p.id])
  const missingIssuer = chosen.some(p => !p.account_code)
  const create = () => {
    run('create', async () => {
      const data = await invoiceApi('/invoices/generate', { po_id: poId,
        selections: chosen.map(p => ({ id: p.id })) })
      toast.success(`${data.invoices.length} draft invoice(s) queued for SBB creation.`)
      closePreview(); await loadHistory()
    })
  }
  const confirmCreate = () => {
    if (missingIssuer) {
      toast.error('Choose an issuing company for every selected draft.')
      return
    }
    if (!enabled) {
      toast.info('SBB draft creation is disabled in this environment.')
      return
    }
    showNotificationPopup({
      message: `Queue ${chosen.length} draft${chosen.length === 1 ? '' : 's'} for SBB creation? They will appear in the invoice list while the background worker creates them. Buyers will not receive an invoice yet.`,
      actionButton: { text: 'Create drafts', color: 'var(--primary-color)' },
      onAction: create,
    })
  }
  const action = (invoice, type) => run(invoice.id, async () => {
    const result = await invoiceApi(`/invoices/${invoice.id}/${type}`, {})
    if (result.error) toast.error(result.error)
    else toast.success(`${labels[result.status] || result.status}${result.number ? ` · ${result.number}` : ''}`)
    await loadHistory()
  })

  return <div className="template">
    <Header>{poId && <Link className="back_button" to={`/purchase-orders/${poId}`}><KeyboardBackspaceIcon /> Go Back</Link>}
      <div className="invoice-header-controls">
      {(kind === 'instalments' || poId) && <div className="invoice-create-controls">
        <button type="button" className="primary-button-header" title="Review eligible drafts before creating them in SBB" disabled={!!busy} onClick={generatePreview}>
          {busy === 'preview' ? 'Loading…' : poId ? 'Refresh preview' : 'Generate drafts'}</button></div>}
    </div></Header>
    <MenuBar />
    <main className="template__body invoice-page">
      {!poId && <nav className="invoice-tabs" aria-label="Invoice type"><Link to="/invoices" className={kind === 'instalments' ? 'active' : ''}>Instalments</Link>
        <Link to="/invoices?kind=po" className={kind === 'po' ? 'active' : ''}>Purchase orders</Link></nav>}
      {proposals !== null ? <section className="body__section invoice-review" aria-label="Draft preview">
        <div className="invoice-section-actions invoice-review-header"><strong>Draft preview</strong><span>{chosen.length} of {proposals.length} selected</span>
          <button type="button" className="invoice-text-button" onClick={closePreview} disabled={!!busy}>Back to invoices</button></div>
        {!enabled && <div className="invoice-review-notice">SBB draft creation is off. This preview has not been saved or sent.</div>}
        <div className="invoice-review-list">
          {!proposals.length && <div className="invoice-empty">No eligible invoices.</div>}
          {proposals.map(p => {
            const totals = invoiceAmounts(p.lines).reduce((sum, row) => sum + row.incl, 0)
            return <div key={p.id} className="invoice-proposal">
              <div className="invoice-proposal-heading">
                <label className="invoice-proposal-buyer"><input type="checkbox" checked={!!selected[p.id]} disabled={!!busy}
                  onChange={e => setSelected(current => ({ ...current, [p.id]: e.target.checked }))} />
                  <span><strong>{p.buyer_name}</strong><small>{p.project_name}</small></span></label>
                <div className="invoice-proposal-company"><span>Issuing company</span><strong>{accounts.find(item => item.id === p.account_code)?.name || p.account_code}</strong></div>
                <div className="invoice-proposal-total"><strong>{formatMoney(totals / 100)}</strong><small>Incl. VAT</small></div>
              </div>
              <InvoiceLines lines={p.lines} />
            </div>
          })}
        </div>
        {!!proposals.length && <div className="invoice-section-actions invoice-review-footer">{missingIssuer && enabled && <span>Choose an issuing company for each selected draft.</span>}
          <button type="button" className="primary-button-header"
          onClick={confirmCreate} disabled={!enabled || !chosen.length || missingIssuer || !!busy}>
          Create {chosen.length} draft{chosen.length === 1 ? '' : 's'} in SBB</button></div>}
      </section> : poId && draftRequested ? <section className="body__section invoice-review" aria-label="Draft preview">
        <div className="invoice-section-actions invoice-review-header"><strong>Draft preview</strong>
          <button type="button" className="invoice-text-button" onClick={closePreview} disabled={!!busy}>Back to invoices</button></div>
        <div className="invoice-preview-state"><span>{poPreviewError || 'Preparing PO draft preview…'}</span>
          {poPreviewError && <button type="button" className="invoice-text-button" onClick={generatePreview} disabled={!!busy}>Try again</button>}</div>
      </section> : <section className="body__section invoice-history-section" aria-label="Invoice history">
        <div className="invoice-section-actions"><span>{total} invoices</span>
          <button type="button" className="invoice-text-button" disabled={!!busy} onClick={() => run('history', loadHistory)}>Refresh</button></div>
        <div className="table invoice-history-table">
          <div className="table__header-6"><div className="table__header--field">Buyer</div><div className="table__header--field">Project</div>
            <div className="table__header--field">Number</div><div className="table__header--field">Issuing company</div>
            <div className="table__header--field">Status</div><div className="table__header--field">Excl. VAT</div></div>
          <div className="table__rows_container">
            {!history.length && <div className="invoice-empty">No invoices yet.</div>}
            {history.map(invoice => <details className="invoice-history" key={invoice.id}>
              <summary className="table__row-6"><span className="table__row--field">{invoice.buyer_name}</span>
                <span className="table__row--field">{invoice.snapshot.project_name}</span>
                <span className="table__row--field">{invoice.number || '—'}</span>
                <span className="table__row--field">{accounts.find(a => a.id === invoice.account_code)?.name || invoice.account_code}</span>
                <span className="table__row--field"><Label title={labels[invoice.status] || invoice.status} labelColor={statusColors[invoice.status] || 'gray'} /></span>
                <span className="table__row--field">{formatMoney(invoice.snapshot.total_excl)}</span></summary>
              <div className="invoice-history-detail"><div className="invoice-meta">Created {formatDate(invoice.created_at)} · Vitruvi ref {invoice.id}
                {invoice.last_synced_at && ` · Synced ${formatDate(invoice.last_synced_at)}`}</div>
                {invoice.error && <div className="invoice-meta">Error: {invoice.error}</div>}
                {['unknown', 'creating'].includes(invoice.status) && <div className="invoice-meta">Check SBB before creating this invoice again.</div>}
                <InvoiceLines lines={invoice.snapshot.lines} />
                <div className="invoice-section-actions"><button type="button" className="invoice-text-button" disabled={!!busy || invoice.status === 'creating'} onClick={() => action(invoice, 'refresh')}>Refresh SBB status</button>
                  {invoice.status === 'failed' && <button type="button" className="invoice-text-button" disabled={!enabled || !!busy} onClick={() => action(invoice, 'retry')}>Retry creation</button>}</div>
              </div>
            </details>)}
          </div>
          {total > 25 && <div className="pagination"><button type="button" aria-label="Previous page" className="pagination__btn" disabled={page <= 1} onClick={() => setPage(page - 1)}><ArrowBackIosIcon /></button>
            <span>{page} / {Math.ceil(total / 25)}</span>
            <button type="button" aria-label="Next page" className="pagination__btn" disabled={page * 25 >= total} onClick={() => setPage(page + 1)}><ArrowForwardIosIcon /></button></div>}
        </div>
      </section>}
    </main>
  </div>
}
